import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IUserState, IOrganization, IProperty, IRole } from '@/types/admin/navigation';
import { setUser, resetAuth } from '../authSlice';
import { UserType } from '@/types/residents/user';

// Initial state matching admin-web exactly
const initialState: IUserState = {
  id: 0,
  email: "",
  firstName: "",
  lastName: "",
  isSuperUser: false,
  organizations: [],
  properties: [],
  selectedOrganizationId: 0,
  selectedPropertyId: 0,
  roles: [],
  isLoading: false,
  emailCode: null,
  accessToken: null,
};

const adminUserSlice = createSlice({
  name: 'adminUser',
  initialState,
  reducers: {
    // Action to set selected property ID
    setSelectedPropertyId: (
      state,
      { payload }: PayloadAction<Pick<IUserState, "selectedPropertyId">>,
    ) => {
      state.selectedPropertyId = payload.selectedPropertyId;
    },
    
    // Action to set selected organization ID
    setSelectedOrganizationId: (
      state,
      { payload }: PayloadAction<Pick<IUserState, "selectedOrganizationId">>,
    ) => {
      state.selectedOrganizationId = payload.selectedOrganizationId;
    },
    
    // Action to clear admin user state
    clearAdminUser: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle user set (admin login path dispatches setUser with user payload)
      .addCase(setUser, (state, { payload }) => {
          const user: any = payload;
          if (!user) return state;
          
          // Determine if user is super user (from type or roles)
          const isSuperUser = user.type === UserType.SUPER_ADMIN || 
                              user.roles?.some((role: any) => role.name === 'Resio Admin') ||
                              false;
          
          // Extract base user data (matching admin-web logic)
          const baseUser = {
            id: user.id,
            email: user.email,
            firstName: user.firstName || "",
            lastName: user.lastName || "",
            isSuperUser,
            roles: user.roles || [],
          };

          // Transform roles to match IRole interface and process organizations/properties
          const transformedRoles: IRole[] = user.roles ? user.roles.map((role: any) => ({
            id: role.id,
            name: role.name,
            organizationId: role.organizationId,
            propertyId: role.propertyId || 0,
            organization: role.organization || { id: role.organizationId, name: 'Unknown Organization' },
            property: role.property || { id: role.propertyId || 0, marketingName: 'Unknown Property', organizationId: role.organizationId },
          })) : [];

          // Process organizations and properties from roles (matching admin-web logic exactly)
          const organizations = transformedRoles.map(role => role.organization);
          const properties = transformedRoles.flatMap(role => {
            if (role.property && role.property.id > 0) {
              return role.property;
            }
            if (role.organization?.properties) {
              return role.organization.properties;
            }
            // Return empty object like admin-web does (this gets filtered out later)
            return {} as IProperty;
          });

          // Remove duplicates (matching admin-web logic exactly)
          const propertyIds: number[] = [];
          const filteredProperties = properties.filter((value: IProperty) => {
            if (!propertyIds.includes(value.id)) {
              propertyIds.push(value.id);
              return value !== null && value.id > 0; // Filter out empty objects and invalid IDs
            }
            return false;
          });

          const orgIds: number[] = [];
          const filteredOrganizations = organizations.filter((value: IOrganization) => {
            // filter null and duplicate organizations
            if (!value || orgIds.includes(value.id)) {
              return false;
            }
            orgIds.push(value.id);
            // filter for organizations with at least one property
            return (
              (value.properties || []).length > 0 ||
              filteredProperties.find((property: IProperty) => property.organizationId === value.id)
            );
          });

          // Set default selected organization and property (matching admin-web logic)
          const selectedOrganizationId = filteredOrganizations[0]?.id || 0;
          const selectedPropertyId =
            filteredProperties.find((property: IProperty) => {
              return property.organizationId === selectedOrganizationId;
            })?.id || filteredProperties[0]?.id || 0;
            
          // Update state with processed data
          return {
            ...state,
            ...baseUser,
            roles: transformedRoles,
            organizations: filteredOrganizations,
            properties: filteredProperties,
            selectedOrganizationId,
            selectedPropertyId,
            isLoading: false,
          };
        })
      // Handle auth reset (logout)
      .addCase(resetAuth, () => {
        return initialState;
      });
  },
});

export const { 
  setSelectedPropertyId, 
  setSelectedOrganizationId, 
  clearAdminUser 
} = adminUserSlice.actions;

export default adminUserSlice.reducer;
